import { homeRegionUrl, isWrongRegion } from './region';

/**
 * Runtime configuration for the generated clients. Apps call
 * `configureApiClient` once at startup (after resolving config + session);
 * generated functions never know about platforms or storage.
 */
export interface ApiClientConfig {
  onearlyBaseUrl: string;
  echoBaseUrl: string | undefined;
  /** Resolves the current bearer token (may refresh). `undefined` = anonymous. */
  getAccessToken: () => Promise<string | undefined>;
  /** Extra headers on every request, e.g. app version for support triage. */
  headers?: Record<string, string>;
  /** Active UI locale, sent as `Accept-Language` so Core can localise content it serves. */
  getLocale?: () => string | undefined;
  /**
   * Mints a new access token whatever the current one's expiry. Called after Core answers
   * `421 WRONG_REGION`, whose usual cause is a token whose home-region claim is missing or
   * stale (platform docs/architecture/multi-region/edge.md 3.4).
   */
  refreshAccessToken?: () => Promise<void>;
  /**
   * Core refused the request because this build is older than it supports
   * (`UNSUPPORTED_APP_VERSION`): the app asks the person to update. The request also fails,
   * with `UnsupportedAppVersionError`.
   */
  onUnsupportedAppVersion?: () => void;
  fetch?: typeof fetch;
}

let config: ApiClientConfig | undefined;

export function configureApiClient(next: ApiClientConfig): void {
  config = next;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly url: string,
    readonly body: unknown,
  ) {
    super(`API ${status} ${url}`);
    this.name = 'ApiError';
  }
}

/**
 * Core refused the request as coming from a build older than it supports. Status 426 (Upgrade
 * Required) whatever Core answered: GraphQL refuses it inside a 200. Nothing was done, and the
 * same request goes through once the app is updated, so a save that fails this way waits.
 */
export class UnsupportedAppVersionError extends ApiError {
  constructor(url: string, body: unknown) {
    super(426, url, body);
    this.name = 'UnsupportedAppVersionError';
  }
}

function requireConfig(): ApiClientConfig {
  if (!config) throw new Error('configureApiClient() must be called before using the API client');
  return config;
}

/**
 * Core's login endpoints and the regions open for sign-up are unauthenticated — sending (or
 * refreshing) a bearer there is wrong, and a bearer would route the regions call by its home.
 */
const PUBLIC_PATHS = ['/api/v1/auth/', '/api/v1/regions'];

async function request<T>(
  baseUrl: string,
  url: string,
  init: RequestInit,
  { followWrongRegion = false }: { followWrongRegion?: boolean } = {},
): Promise<T> {
  const cfg = requireConfig();
  const isPublic = PUBLIC_PATHS.some((prefix) => url.startsWith(prefix));
  const token = isPublic ? undefined : await cfg.getAccessToken();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  // A FormData body (file uploads) must set its own multipart boundary — never force JSON on it.
  const isMultipart = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (init.body && !isMultipart && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const locale = cfg.getLocale?.();
  if (locale) headers.set('Accept-Language', locale);
  for (const [k, v] of Object.entries(cfg.headers ?? {})) headers.set(k, v);

  const doFetch = cfg.fetch ?? fetch;
  let response = await doFetch(`${baseUrl}${url}`, { ...init, headers });
  let text = await response.text();
  let data: unknown = text ? safeJson(text) : undefined;
  if (followWrongRegion && response.status === 421 && isWrongRegion(response, data)) {
    // Edge.md 3.4: the region that answered is not the person's home. Retry once at the home's
    // own name with the same method, body and headers, so a write keeps its idempotency key;
    // the region refused before doing anything, so nothing runs twice. A second 421 is the
    // caller's error. Then refresh the token, whose home claim is the usual cause, even when
    // the retry fails, and let later requests go to the configured name again.
    try {
      const home = homeRegionUrl(response, data);
      if (home) {
        response = await doFetch(`${home}${url}`, { ...init, headers });
        text = await response.text();
        data = text ? safeJson(text) : undefined;
      }
    } finally {
      await cfg.refreshAccessToken?.().catch(() => undefined);
    }
  }
  if (refusesAppVersion(data)) {
    cfg.onUnsupportedAppVersion?.();
    throw new UnsupportedAppVersionError(url, data);
  }
  if (!response.ok) throw new ApiError(response.status, url, data);
  // Orval's fetch client expects `{ data, status, headers }`.
  return { data, status: response.status, headers: response.headers } as T;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

/**
 * Core's answer to a build below its minimum: `code: UNSUPPORTED_APP_VERSION` in a REST error
 * body, or in a GraphQL error's `extensions` (core `src/platform/graphql/index.ts`).
 */
function refusesAppVersion(data: unknown): boolean {
  const body = asRecord(data);
  if (body.code === 'UNSUPPORTED_APP_VERSION') return true;
  return (Array.isArray(body.errors) ? body.errors : []).some(
    (error) => asRecord(asRecord(error).extensions).code === 'UNSUPPORTED_APP_VERSION',
  );
}

/** Orval mutator for the OnEarly Core contract. */
export const onearlyRequest = <T>(url: string, init: RequestInit): Promise<T> =>
  request<T>(requireConfig().onearlyBaseUrl, url, init, { followWrongRegion: true });

/** Orval mutator for echo's own API. */
export const echoRequest = <T>(url: string, init: RequestInit): Promise<T> => {
  const base = requireConfig().echoBaseUrl;
  if (!base) throw new Error('echoBaseUrl is not configured for this environment');
  return request<T>(base, url, init);
};

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
